import {
  updateCart, updateCartState, updateCartTotal, updateCartItem,
  openCartDrawer, setCartMessage,
} from './cart-drawer';
import { QuantitySelector } from './quantity-selector';
import { openModal, closeModal } from './modal';
import { disableInputs, wait } from './utils';
import { hasIncompletePromos } from './incomplete-promos';
import { cartHasRestrictedItems } from './restricted-items';
import EcommerceAnalytics from './ecommerce-analytics';

const eCommerceAnalytics = new EcommerceAnalytics();

export function setCartLoading(loading: boolean) {
  document.querySelectorAll<HTMLElement>('.js-cart-loading-wrapper').forEach((el) => {
    el.classList.toggle('is-loading', loading);
  });
}

export async function fetchCart(render?: string): Promise<any> {
  try {
    const response = await fetch(`/api/cart?render=${render}`);
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }
    const data = await response.json();
    console.debug('[cart] fetchCart (render=%s) →', render, data.cart);
    return data;
  } catch (error) {
    console.error('Error fetching cart:', error);
  }
}

export async function addToCart(
  itemOrItems: CartItem | CartItem[],
  csrf: string,
  openCart?: boolean,
  message?: string,
): Promise<{ promoChooser: boolean }> {
  try {
    const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];

    const response = await fetch('/api/cart?render=compact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
        _csrf: csrf,
        // Uncomment to skip the stock check (for testing purposes, requires STAGING=true environment variable)
        // skipStockCheck: true,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    const cartData = await response.json();

    // ATC promo chooser (§4): the server may decline to add and instead ask the user to pick which
    // promo this product applies to. Hand the rendered chooser body to the drawer module and stop —
    // nothing was added, so don't touch the cart UI. The user's pick re-adds the item (pinned).
    if (cartData?.promoChooser?.html) {
      window.dispatchEvent(new CustomEvent('promoChooser:open', {
        detail: { html: cartData.promoChooser.html },
      }));
      return { promoChooser: true };
    }

    console.debug('[cart] addToCart →', cartData.cart);

    // Server-provided analytics payload (contains actual added quantities and resolved prices).
    // If none present, skip analytics tracking.
    if (cartData?.analytics?.added?.length) {
      // Determine indexName once for the whole event (same heuristic as the tracker used previously)
      const indexName =
        document.getElementById('sort-by')?.querySelector<HTMLSelectElement>('.ais-SortBy-select')?.value ?? undefined;
      // Determine queryID once for the whole event from URL or hits widget data attribute
      const queryID = new URLSearchParams(window.location.search).get('qid')
        ?? document.getElementById('hits')?.querySelector<HTMLElement>('.product-card')?.dataset.queryId
        ?? undefined;

      eCommerceAnalytics.trackAddToCart({
        items: cartData.analytics.added,
        cartItems: cartData.analytics.cart,
        indexName,
        queryID,
      }).catch((err) => {
        console.debug('Analytics tracking failed', err);
      });
    }

    // Update cart in sidebar and open
    updateCart(cartData);
    updateCartCount(cartData);

    dispatchCartUpdate(cartData);

    if (message) {
      setCartMessage(message);
    }

    if (openCart) {
      openCartDrawer();
    }

    return { promoChooser: false };
  } catch (error) {
    console.error('Error adding item to cart:', error);
    return { promoChooser: false };
  }
}

export async function removeFromCart(cartItemId: string, csrf: string, render?: string): Promise<any> {
  try {
    const url = render ? `/api/cart?render=${render}` : '/api/cart';
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: cartItemId,
        _csrf: csrf,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove item from cart');
    }

    const cartData = await response.json();
    console.debug('[cart] removeFromCart →', cartData.cart);

    // Server-provided analytics payload (contains actual removed quantities and resolved prices).
    // If none present, skip analytics tracking.
    if (cartData?.analytics?.removed?.length) {
      eCommerceAnalytics.trackRemoveFromCart({
        items: cartData.analytics.removed,
      }).catch((err) => {
        console.debug('Analytics tracking failed', err);
      });
    }

    if (Object.keys(cartData.cart.items).length === 0) {
      updateCartState('empty');
    }

    updateCartTotal(cartData);
    updateCartCount(cartData);

    dispatchCartUpdate(cartData);

    return cartData;
  } catch (error) {
    console.error('Error adding item to cart:', error);
  }
}

export async function increment(cartItemId: string, quantity: number = 1, csrf: string, render?: string): Promise<any> {
  try {
    const url = render ? `/api/cart/increment?render=${render}` : '/api/cart/increment';
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: cartItemId,
        quantity,
        _csrf: csrf,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to increment item in cart');
    }

    const cartData = await response.json();
    console.debug('[cart] increment →', cartData.cart);
    if (cartData.html) {
      updateCart(cartData);
    } else {
      updateCartItem(cartData, cartItemId);
    }
    updateCartTotal(cartData);
    updateCartCount(cartData);

    dispatchCartUpdate(cartData);

    return cartData;
  } catch (error) {
    console.error('Error adding item to cart:', error);
  }
}

export async function decrement(cartItemId: string, quantity: number = 1, csrf: string, render?: string): Promise<any> {
  try {
    const url = render ? `/api/cart/decrement?render=${render}` : '/api/cart/decrement';
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: cartItemId,
        quantity,
        _csrf: csrf,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to decrement item in cart');
    }

    const cartData = await response.json();
    console.debug('[cart] decrement →', cartData.cart);

    if (cartData.html) {
      updateCart(cartData);
    } else {
      updateCartItem(cartData, cartItemId);
    }
    updateCartTotal(cartData);
    updateCartCount(cartData);

    dispatchCartUpdate(cartData);

    return cartData;
  } catch (error) {
    console.error('Error removing item from cart:', error);
  }
}

export async function setQuantity(cartItemId: string, quantity: number, csrf: string, render?: string): Promise<any> {
  try {
    const url = render ? `/api/cart/quantity?render=${render}` : '/api/cart/quantity';
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: cartItemId,
        quantity,
        _csrf: csrf,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to adjust item in cart');
    }

    const cartData = await response.json();
    console.debug('[cart] setQuantity →', cartData.cart);
    if (cartData.html) {
      updateCart(cartData);
    } else {
      updateCartItem(cartData, cartItemId);
    }
    updateCartTotal(cartData);
    updateCartCount(cartData);

    dispatchCartUpdate(cartData);

    return cartData;
  } catch (error) {
    console.error('Error adjusting quantity:', error);
  }
}

export function updateCartCount(cart: any) {
  const cartNumItems = document.querySelectorAll('.js-cart-num-items');
  if (!cartNumItems) {
    return;
  }
  const count = cart.cart.totalItems;

  if (count && cartNumItems) {
    cartNumItems.forEach((elem) => {
      elem.innerHTML = count > 10 ? '10<sup>+</sup>' : `${count}`;
    });
  } else {
    cartNumItems.forEach((elem) => {
      elem.innerHTML = '';
    });
  }
}

export function attachCartEventListeners(elem: HTMLElement, render?: string) {
  if (!elem) {
    return;
  }

  const quantitySelectors = elem.querySelectorAll<HTMLElement>('.js-quantity-selector');
  const removeButtons = elem.querySelectorAll<HTMLButtonElement>('.js-remove-item');
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  const applyCartUpdates = (cartData: any) => {
    updateCart(cartData);
    updateCartTotal(cartData);
    updateCartCount(cartData);
    dispatchCartUpdate(cartData);
  };

  quantitySelectors?.forEach((selector) => {
    const quantitySelector = new QuantitySelector(selector);

    selector.addEventListener('quantityChange', async (event: any) => {
      const quantity = event.detail.quantity;
      const cartItem = (event.target as HTMLElement).closest('.js-cart-item') as HTMLElement;
      if (cartItem) {
        cartItem.classList.add('processing');
        disableInputs(cartItem, true);
        setCartLoading(true);
        const id = cartItem.dataset.id;
        const cartItemId = cartItem.dataset.cartItemId;

        if (!id || !cartItemId) {
          return;
        }

        let cart;

        // If its 0, don't do anything, they should use the remove button
        if (quantity === 0) {
          // cart = await removeFromCart(cartItemId, csrfToken);
          return;
        } else {
          cart = await setQuantity(cartItemId, quantity, csrfToken, render);
        }

        if (!cart) {
          setCartLoading(false);
          disableInputs(cartItem, false);
          quantitySelector.updateButtonStates();
          cartItem.classList.remove('processing');
          return;
        }

        if (cart.cart.items) {
          setCartMessage('Quantity updated');
          updateCartCount(cart);

          if (quantity !== 0) {
            disableInputs(cartItem, false);
            quantitySelector.updateButtonStates();
            cartItem.classList.remove('processing');
          } else {
            cartItem.remove();
          }
        }
      }
    });
  });

  removeButtons?.forEach((button) => {
    button.addEventListener('click', async (event) => {
      const cartItem = (event.target as HTMLElement).closest('.js-cart-item') as HTMLElement;
      if (cartItem) {
        disableInputs(cartItem, true);
        setCartLoading(true);
        const id = cartItem.dataset.id;
        const cartItemId = cartItem.dataset.cartItemId;
        const qty = cartItem.querySelector<HTMLInputElement>('[name="quantity"]')?.value || '1';

        if (!id || !cartItemId) {
          return;
        }

        cartItem.classList.add('processing');

        const cart = await removeFromCart(cartItemId, csrfToken, render);

        if (!cart) {
          setCartLoading(false);
          disableInputs(cartItem, false);
          cartItem.classList.remove('processing');
          return;
        }

        await wait(1000);

        cartItem.remove();

        updateCart(cart);

        setCartMessage(`${qty} ${parseInt(qty) === 1 ? 'item' : 'items'} removed`);

        if (Object.keys(cart.cart.items).length === 0) {
          updateCartState('empty');
        }
      }
    });
  });

  // "No thanks" on auto-added reward items — dismisses the promo and removes reward items
  const declineAutoRewardButtons = elem.querySelectorAll<HTMLButtonElement>('.js-decline-auto-reward');
  declineAutoRewardButtons?.forEach((button) => {
    button.addEventListener('click', async () => {
      const promoId = parseInt(button.dataset.promoId ?? '', 10);
      if (!promoId) {
        return;
      }
      button.disabled = true;
      setCartLoading(true);
      try {
        const res = await fetch(`/api/cart/dismiss-promo?render=${render ?? 'normal'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
          body: JSON.stringify({ promoId }),
        });
        if (!res.ok) {
          throw new Error('Failed to dismiss promo');
        }
        const cartData = await res.json();
        if (cartData) {
          applyCartUpdates(cartData);
        }
      } catch (error) {
        button.disabled = false;
        setCartLoading(false);
        console.error('Error dismissing promo:', error);
      }
    });
  });

  // "Add back in" on dismissed promo groups — un-dismisses and lets validate re-add the reward
  const addBackButtons = elem.querySelectorAll<HTMLButtonElement>('.js-addback-reward');
  addBackButtons?.forEach((button) => {
    button.addEventListener('click', async () => {
      const promoId = parseInt(button.dataset.promoId ?? '', 10);
      if (!promoId) {
        return;
      }
      button.disabled = true;
      setCartLoading(true);
      try {
        const res = await fetch(`/api/cart/undismiss-promo?render=${render ?? 'normal'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
          body: JSON.stringify({ promoId }),
        });
        if (!res.ok) {
          throw new Error('Failed to add promo back in');
        }
        const cartData = await res.json();
        if (cartData) {
          applyCartUpdates(cartData);
        }
      } catch (error) {
        button.disabled = false;
        setCartLoading(false);
        console.error('Error adding promo back in:', error);
      }
    });
  });
}

/** Submit an 'add-to-cart' form */
export async function submitAddToCartForm(form: HTMLFormElement) {
  const formData = new FormData(form);
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  const productId = formData.get('id') as string;
  const quantityBreakString = formData.get('quantityBreak') as string;
  const quantityBreak = quantityBreakString ? parseInt(quantityBreakString, 10) : undefined;
  const quantity = parseInt(formData.get('quantity') as string, 10) || 1;
  const submitBtn = form.querySelector<HTMLButtonElement>('[type="submit"]');

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.classList.add('processing');
  }

  if (!productId) {
    console.error('Product ID is missing.');
    return;
  }

  const message = quantity > 1
    ? `${quantity} items added to cart`
    : '1 item added to cart';

  try {
    const result = await addToCart({ id: productId, quantity, quantityBreak }, csrfToken, true, message);

    // If the server opened the promo chooser instead of adding, skip the "added" confirmation —
    // nothing was added yet (the add happens when the user picks a promo and proceeds).
    if (!result?.promoChooser) {
      submitBtn?.classList.add('finished');

      // Remove after 2 seconds
      setTimeout(() => {
        submitBtn?.classList.remove('finished');
      }, 2000);
    }
  } catch (err) {
    console.error(err);
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.classList.remove('processing');
    }
  }
}

function dispatchCartUpdate(cartData: any) {
  window.dispatchEvent(new CustomEvent('cart:update', {
    detail: cartData,
  }));
}

export function checkCartIsValid(cartData: any) {
  const { cart } = cartData;
  const { items } = cart;

  if (!Object.keys(items).length) {
    return false;
  }

  const itemList = Object.values(items) as Array<any>;

  for (const item of itemList) {
    if (item.outOfStock) {
      return false;
    }
  }

  return true;
}

// In-flight confirms, one per modal — rapid double-clicks (or the page button + drawer link) share
// one modal, so they must share one promise instead of stacking listeners/observers on it.
const pendingConfirms: Record<string, Promise<boolean> | undefined> = {};

/**
 * Open a confirmation modal and resolve true when its proceed button is clicked (and
 * `beforeProceed`, if given, resolves true), or false when it's dismissed.
 */
function confirmWithModal(
  name: string,
  modal: HTMLElement,
  proceedBtn: HTMLElement | null,
  beforeProceed?: () => Promise<boolean>,
): Promise<boolean> {
  const pending = pendingConfirms[name];
  if (pending) {
    return pending;
  }

  const promise = new Promise<boolean>((resolve) => {
    let settled = false;

    const settle = (result: boolean) => {
      settled = true;
      delete pendingConfirms[name];
      observer.disconnect();
      proceedBtn?.removeEventListener('click', onProceed);
      resolve(result);
    };

    const onProceed = async () => {
      if (beforeProceed && !(await beforeProceed())) {
        return;
      }
      if (!settled) {
        settle(true);
        closeModal(name);
      }
    };

    // Every dismissal path (Go back, X, overlay, Escape) goes through the modal
    // system's close, which removes the visibility class — watch for it.
    const observer = new MutationObserver(() => {
      if (!settled && !modal.classList.contains('modal--is-visible')) {
        settle(false);
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });

    proceedBtn?.addEventListener('click', onProceed);
    openModal(name);
  });

  pendingConfirms[name] = promise;
  return promise;
}

export function confirmIncompletePromos(cartData: any): Promise<boolean> {
  if (!hasIncompletePromos(cartData)) {
    return Promise.resolve(true);
  }

  const modal = document.querySelector<HTMLElement>('.js-modal[data-modal="incomplete-promo"]');
  if (!modal) {
    // Modal shell missing — fail open rather than block checkout.
    return Promise.resolve(true);
  }

  return confirmWithModal(
    'incomplete-promo',
    modal,
    modal.querySelector<HTMLButtonElement>('.js-incomplete-promo-proceed'),
  );
}

/**
 * The pesticides "Attention" modal, shown every time a licensed customer heads to checkout with a
 * restricted item. Continue needs both acknowledgements ticked, then POSTs them: the server won't
 * let /checkout through without that (CheckoutController.index), so unlike the incomplete-promo
 * confirm this one doesn't fail open.
 */
export function confirmRestrictedItems(cartData: any): Promise<boolean> {
  if (!cartHasRestrictedItems(cartData)) {
    return Promise.resolve(true);
  }

  const modal = document.querySelector<HTMLElement>('.js-modal[data-modal="restricted-items"]');
  if (!modal) {
    return Promise.resolve(false);
  }

  const pending = pendingConfirms['restricted-items'];
  if (pending) {
    return pending;
  }

  const acks = Array.from(modal.querySelectorAll<HTMLInputElement>('.js-restricted-items-ack'));
  const continueBtn = modal.querySelector<HTMLButtonElement>('.js-restricted-items-continue');
  const errorText = modal.querySelector<HTMLElement>('.js-restricted-items-error');

  const updateContinue = () => {
    if (continueBtn) {
      continueBtn.disabled = !acks.every((ack) => ack.checked);
    }
  };

  // Nothing is remembered: every opening starts unticked.
  acks.forEach((ack) => {
    ack.checked = false;
    ack.onchange = updateContinue;
  });
  updateContinue();
  if (errorText) {
    errorText.hidden = true;
  }

  const acknowledge = async () => {
    if (continueBtn) {
      continueBtn.disabled = true;
    }
    if (errorText) {
      errorText.hidden = true;
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch('/api/cart/restricted-acknowledgement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        body: JSON.stringify({
          readPolicy: acks[0]?.checked ?? false,
          overEighteenWithLicence: acks[1]?.checked ?? false,
        }),
      });
      if (!res.ok) {
        throw new Error('Failed to acknowledge restricted items');
      }
      return true;
    } catch (error) {
      console.error(error);
      if (errorText) {
        errorText.hidden = false;
      }
      updateContinue();
      return false;
    }
  };

  return confirmWithModal('restricted-items', modal, continueBtn, acknowledge);
}

/**
 * Every "go to checkout" button runs this before leaving the cart: incomplete promos first, then
 * the restricted-items acknowledgement. Use it for any new checkout entry point.
 */
export async function confirmCheckout(cartData: any): Promise<boolean> {
  return (await confirmIncompletePromos(cartData)) && confirmRestrictedItems(cartData);
}
